/**
 * Starting the layout worker under Trusted Types (decision 44; part 01, finding 2). The app's CSP requires Trusted Types
 * for scripts, and a worker's script URL is a script sink: Chromium refuses `new Worker(url)` unless the URL comes from
 * a policy the CSP names. This module holds the app's one policy, which lets through only same-origin URLs of the layout
 * worker's, the bundle worker's (part 24a), the backup worker's (part 24e), the image worker's (part 16a), the import worker's (part 17), the find worker's (part 19a), the EPUB worker's (part 20), the KDF worker's (part 25a) and the copy's sealing worker's (part 38b) bundles, and the service worker's script (part 40a; registering one is a
 * script sink too). TypeScript's DOM library has no Trusted Types declarations, so this is a boundary: the browser's
 * `trustedTypes` is checked by shape, and the `TrustedScriptURL` it returns is typed as the string it stands for, which
 * is what `new Worker` accepts. Table T in the part 15 log.
 */

/** The policy name the app's CSP allows (`trusted-types cockatrice-worker`). */
export const WORKER_POLICY = 'cockatrice-worker';

/** T1: the layout, bundle, backup, image, import, find, EPUB, DOCX, KDF and sealing workers' bundles, in dev (their source) and in a build (hashed). */
const WORKER_FILE = /^(?:layout|bundle|backup|images|import|find|epub|docx|kdf|seal)\.worker(?:\.ts|-[\w-]+\.js)$/u;

/** T8: the service worker's script, at the origin's root, with no query (part 40a). */
const SERVICE_WORKER = '/sw.js';

/** Thrown for a worker URL the policy refuses. */
export class WorkerUrlError extends Error {
  override readonly name = 'WorkerUrlError';
}

/**
 * T1–T5, T8: returns the URL when it is an http(s) URL on `origin` naming a worker's bundle, with any query (Vite's
 * dev server adds one), or the service worker's script with none; throws {@link WorkerUrlError} for another scheme (a
 * `blob:` URL carries its creator's origin) or origin, another file, a fragment or credentials.
 */
export function checkWorkerUrl(url: string, origin: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url, origin);
  } catch {
    throw new WorkerUrlError(`not a URL: ${url.slice(0, 200)}`);
  }
  const file = parsed.pathname.slice(parsed.pathname.lastIndexOf('/') + 1);
  const web = parsed.protocol === 'https:' || parsed.protocol === 'http:';
  const named = WORKER_FILE.test(file) || (parsed.pathname === SERVICE_WORKER && parsed.search === '');
  if (!web || parsed.origin !== origin || !named || parsed.hash !== '' || parsed.username !== '' || parsed.password !== '') {
    throw new WorkerUrlError(`not one of the app's workers: ${url.slice(0, 200)}`);
  }
  return parsed.href;
}

interface TrustedTypePolicyFactory {
  createPolicy(name: string, rules: { createScriptURL: (input: string) => string }): { createScriptURL(input: string): unknown };
}

function isFactory(value: unknown): value is TrustedTypePolicyFactory {
  return typeof value === 'object' && value !== null && typeof Reflect.get(value, 'createPolicy') === 'function';
}

/**
 * T6, T7: makes the function that turns the worker bundle's URL into what `new Worker` accepts. With Trusted Types
 * (`trustedTypes` is the browser's `globalThis.trustedTypes`), it creates the {@link WORKER_POLICY} policy once and
 * returns its `TrustedScriptURL`s; without, it returns the checked URL. Either way a URL the rule refuses throws.
 */
export function workerUrlFactory(trustedTypes: unknown, origin: string): (url: string) => string {
  if (!isFactory(trustedTypes)) return (url) => checkWorkerUrl(url, origin);
  const policy = trustedTypes.createPolicy(WORKER_POLICY, { createScriptURL: (input) => checkWorkerUrl(input, origin) });
  // The browser's TrustedScriptURL, which `new Worker` takes where a string URL is typed.
  return (url) => policy.createScriptURL(url) as string;
}
